/**
 * v11.6 V116-4-4 (ADR-0114, design round V116-4-1): pure helpers for the insurability pack's legal section (format /3).
 * Shared by the internal pack page and the keyed verify page. Wording follows the design round:
 * "in scope", "Triggered by", "mapped controls passing", "Counted / Not counted while paused".
 */
import type { PackLawRow, PackLegalChanges, PackLegalControlRow, PackLegalSection, PrivilegeInclusionReason } from './api';
import { fmtDate, parseDisplayDate, plural } from './format';

const PASS = new Set(['pass', 'excepted']);
export const isPassing = (state: string) => PASS.has(state);

export const CONTROL_STATE_LABEL: Record<string, string> = {
  pass: 'Passing', excepted: 'Excepted', fail: 'Failing', no_evidence: 'No evidence', insufficient: 'Not enough evidence',
  stale: 'Evidence out of date', couldnt_check: 'Couldn’t check',
};
export const controlStateLabel = (s: string) => CONTROL_STATE_LABEL[s] ?? 'Not checked';

/** The law's standing as a reader sees it. A stayed law is labelled by `stay_kind` (owner, 2026-10-27). */
export function packLawStatusLabel(l: PackLawRow): string {
  switch (l.standing) {
    case 'stayed': return l.stay_kind === 'standstill' ? 'Enforcement paused' : l.stay_kind === 'court_order' ? 'Stayed by court' : 'Stayed';
    case 'upcoming': return l.effective_date ? `Starts ${fmtDate(l.effective_date)}` : 'Upcoming';
    case 'vacated': return 'Vacated';
    case 'annulled': return 'Annulled';
    case 'pending': return 'Pending';
    case 'settled': return 'Settled';
    default: return 'In force';
  }
}

/** How the law is counted. Null for an ordinary counted law (nothing to say). */
export function packLawCountingLabel(l: PackLawRow): string | null {
  if (l.counted) return l.standing === 'stayed' ? 'Counted while paused' : null;
  switch (l.not_counted_reason) {
    case 'paused': return 'Not counted while paused';
    case 'decision_needed': return 'Decision needed';
    case 'does_not_apply': return l.standing === 'stayed' ? 'Not counted while paused' : 'Not counted · does not apply';
    case 'struck': return 'Struck · counts nowhere';
    default: return 'Not counted';
  }
}

/** "N of M mapped controls passing". */
export const mappedPassingText = (l: PackLawRow) =>
  l.controls.of === 0 ? 'No mapped controls' : `${l.controls.passing} of ${l.controls.of} mapped controls passing`;

/** Scope never shrinks: the headline count is every law listed; counting is a separate clause. */
export function scopeHeadline(legal: Pick<PackLegalSection, 'laws'>): { count: number; counted: number; notCounted: number; text: string } {
  const count = legal.laws.length;
  const counted = legal.laws.filter((l) => l.counted).length;
  const notCounted = count - counted;
  const clause = notCounted ? `${counted} counted · ${notCounted} not counted` : count ? 'All counted' : 'No AI law in scope';
  return { count, counted, notCounted, text: clause };
}

/** "Bias audit · NYC LL144 · ISO 42001 · 2 cases". Not-counted laws are left off (they back nothing counted). */
export function tagLineText(r: PackLegalControlRow): string {
  const laws = r.tags.laws.filter((l) => l.counted).map((l) => l.short_name);
  const parts = [...laws, ...r.tags.frameworks];
  if (r.tags.cases) parts.push(plural(r.tags.cases, 'case'));
  return parts.join(' · ');
}

/** The local calendar day of an ISO date or timestamp, as YYYY-MM-DD. */
function dayOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** LIVE, never signed (ADR-0114 §4): a fix-by date before today is overdue. */
export function isOverdue(fixBy: string | null | undefined, now: Date = new Date()): boolean {
  const d = parseDisplayDate(fixBy ?? null);
  return !!d && dayOf(d) < dayOf(now);
}

export type ControlGroup = 'open' | 'passing' | 'paused' | 'no_source';
/** Which part of the controls list a row sits in: open gaps first, then the fold (passing, paused law, no source). */
export function controlGroup(r: PackLegalControlRow): ControlGroup {
  if (r.no_current_legal_source) return 'no_source';
  if (!r.counted) return 'paused';
  return isPassing(r.state) ? 'passing' : 'open';
}

export interface ChangeLines {
  /** Version compared against (null = first version). */
  since: number | null;
  comparable: boolean;
  performance: Array<{ text: string; direction: 'better' | 'worse' }>;
  counting: string[];
}

/** Change since the previous signed version: performance (state) apart from counting (what is counted). */
export function changeLines(changes: PackLegalChanges | null | undefined, titles: Map<string, string>): ChangeLines | null {
  if (!changes || changes.previous_version == null) return null;
  const performance = changes.performance.map((p) => ({
    direction: p.direction,
    text: `${titles.get(p.control_id) ?? p.control_id}: now ${controlStateLabel(p.to).toLowerCase()}`,
  }));
  const verb: Record<PackLegalChanges['counting'][number]['kind'], string> = {
    law_added: 'now in scope', law_removed: 'no longer in scope', law_counted: 'now counted', law_not_counted: 'now not counted',
    control_added: 'now listed', control_removed: 'no longer listed',
  };
  const counting = changes.counting.map((c) => `${c.label}: ${verb[c.kind]}`);
  return { since: changes.previous_version, comparable: changes.comparable, performance, counting };
}

export const PRIVILEGE_REASON_LABEL: Record<PrivilegeInclusionReason, string> = {
  insurer_request: 'The insurer asked for it',
  renewal_underwriting: 'Renewal underwriting',
  claim_support: 'Supporting a claim',
  broker_request: 'The broker asked for it',
};
